
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import {
  ProfileMapInterface,
  ReviewCommentInterface,
  WSTicketCommentInterface,
} from '@/services/QC-V2/chat/interfaces';
import DossierFeedbackRepositoryInterface, {
  AgentFeedbackInterface,
  AggregatedFeedbackChatComments,
  AggregatedFeedbackTicketComments,
  FeedbackLogInterface,
} from '@/services/Dossier/feedback/interfaces';
import DeleteFeedbackDialog from '@/components/dialogs/dossier/feedback/DeleteFeedbackDialog.vue';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { VueEditor } from 'vue2-editor';

@Component({
  components: { DeleteFeedbackDialog, VueEditor },
  mixins: [
    Themeable,
  ],
  inject: [
    'profileRepository',
    'dossierFeedbackRepository',
    'tagRepositoryV2',
  ],
  sockets: {
    feedbackChatComments(msg: { comments: ReviewCommentInterface }): void {
      this.$emit('feedback-chat-comments', msg.comments);
    },
    feedbackTicketComments(msg: { comments: WSTicketCommentInterface }): void {
      this.$emit('feedback-ticket-comments', msg.comments);
    },
  },
})
export default class ViewFeedback extends Vue {
  private dossierFeedbackRepository!: DossierFeedbackRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;

  private loading: boolean = true;

  private feedbackId!: number;
  private feedback!: AgentFeedbackInterface;
  private profilesMap: ProfileMapInterface = {};
  private tagsMap: { [key: number]: TagInterface } = {};

  private feedbackChatComments: AggregatedFeedbackChatComments[] = [];
  private feedbackTicketComments: AggregatedFeedbackTicketComments[] = [];

  private lastLog!: FeedbackLogInterface;

  private showLogs: boolean = false;
  private showDeleteDialog: boolean = false;

  private beforeCreate(): void {
    this.$on(
      'feedback-chat-comments',
      (comments: ReviewCommentInterface[]) => this.handleChatCommentsMessage(comments),
    );

    this.$on(
      'feedback-ticket-comments',
      (comments: WSTicketCommentInterface[]) => this.handleTicketCommentsMessage(comments),
    );
  }

  private async created(): Promise<void> {
    this.feedbackId = Number(this.$route.params.feedbackId);
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      this.feedback = await this.dossierFeedbackRepository.getFeedback(this.feedbackId);
      await this.fetchProfiles();
      await this.fetchTags();
      this.fetchLastLog();
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  private fetchLastLog(): void {
    if (!this.feedback.logs || !this.feedback.logs.length) {
      this.lastLog = {
        id: -1,
        userId: this.feedback.from.id,
        date: this.feedback.date,
        action: '',
      };

      return;
    }

    this.lastLog = this.feedback.logs.sort((logA, logB) => logB.id - logA.id)[0];
  }

  private async fetchProfiles(): Promise<void> {
    const ids: string[] = [];
    if (!ids.includes(this.feedback.to.id)) {
      ids.push(this.feedback.to.id);
    }

    if (!ids.includes(this.feedback.from.id)) {
      ids.push(this.feedback.from.id);
    }

    if (this.feedback.logs && this.feedback.logs.length) {
      this.feedback.logs.forEach((log) => {
        if (!ids.includes(log.userId)) {
          ids.push(log.userId);
        }
      });
    }

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });
  }

  private async fetchTags(): Promise<void> {
    const tags = await this.tagRepositoryV2.getAllTags();
    tags.forEach((tag) => {
      this.tagsMap[tag.id] = tag;
    });
  }

  private async handleChatCommentsMessage(comments: ReviewCommentInterface[]): Promise<void> {
    if (this.feedbackChatComments.length) {
      return;
    }

    if (!this.feedback) {
      setTimeout(() => {
        this.handleChatCommentsMessage(comments);
      }, 200);
      return;
    }

    const ids: string[] = [];
    const feedbackChatComments: AggregatedFeedbackChatComments[] = [];
    comments.forEach((comment) => {
      const index = this.feedback.chatComments.findIndex((c) => c.commentId === comment.id);
      if (index === -1) {
        return;
      }

      let feedbackChatIndex = feedbackChatComments.findIndex((chatComment) => chatComment.chatId === this.feedback.chatComments[index].chatId);
      if (feedbackChatIndex === -1) {
        feedbackChatComments.push({
          chatId: this.feedback.chatComments[index].chatId,
          comments: [],
          tags: [],
        });

        feedbackChatIndex = feedbackChatComments.length - 1;
      }

      feedbackChatComments[feedbackChatIndex].tags = comment.review?.tags;
      feedbackChatComments[feedbackChatIndex].comments.push({
        id: comment.id,
        author: comment.author,
        text: comment.text,
        timestamp: comment.timestamp,
        rate: comment.rate,
      });

      feedbackChatComments[feedbackChatIndex].comments = feedbackChatComments[feedbackChatIndex]
        .comments
        .sort((commentA, commentB) => new Date(commentA.timestamp).getTime() - new Date(commentB.timestamp).getTime());

      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });

    this.feedbackChatComments = feedbackChatComments;
  }

  private async handleTicketCommentsMessage(comments: WSTicketCommentInterface[]): Promise<void> {
    if (this.feedbackTicketComments.length) {
      return;
    }

    if (!this.feedback) {
      setTimeout(() => {
        this.handleTicketCommentsMessage(comments);
      }, 200);
      return;
    }

    const ids: string[] = [];
    const feedbackTicketComments: AggregatedFeedbackTicketComments[] = [];
    comments.forEach((comment) => {
      const index = this.feedback.ticketComments.findIndex((c) => c.commentId === comment.id);
      if (index === -1) {
        return;
      }

      let feedbackTicketIndex = feedbackTicketComments.findIndex((feedbackTicketComment) => feedbackTicketComment.ticketId === comment.ticketId);
      if (feedbackTicketIndex === -1) {
        feedbackTicketComments.push({
          ticketId: comment.ticketId,
          ticketNumber: this.feedback.ticketComments[index].ticketNumber,
          comments: [],
          tags: [],
        });

        feedbackTicketIndex = feedbackTicketComments.length - 1;
      }

      feedbackTicketComments[feedbackTicketIndex].tags = comment.review?.tags;
      feedbackTicketComments[feedbackTicketIndex].comments.push({
        id: comment.id,
        author: comment.author,
        text: comment.text,
        timestamp: comment.timestamp,
        rate: comment.rate,
      });

      feedbackTicketComments[feedbackTicketIndex].comments = feedbackTicketComments[feedbackTicketIndex]
        .comments
        .sort((commentA, commentB) => new Date(commentA.timestamp).getTime() - new Date(commentB.timestamp).getTime());


      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });

    this.feedbackTicketComments = feedbackTicketComments;
  }

  private async updateFeedback(): Promise<void> {
    await this.$router.push({
      name: 'update-feedback',
      params: { userId: this.feedback.to.id, feedbackId: String(this.feedback.id) },
    });
  }

  private async deleteFeedback(): Promise<void> {
    try {
      await this.dossierFeedbackRepository.deleteFeedback(this.feedback.id);
      this.$successDialog.show('Feedback Deleted Successfully');
      await this.goBack();
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  private async goBack(): Promise<void> {
    await this.$router.push({ name: 'agent-profile', params: { userId: String(this.feedback.to.id) } });
  }
}
